import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';

interface AddPracticeFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: { tin: string; tinName: string; practiceAlias?: string }) => Promise<void>;
  organizationName: string;
}

export function AddPracticeForm({ open, onOpenChange, onSubmit, organizationName }: AddPracticeFormProps) {
  const [tin, setTin] = useState('');
  const [tinName, setTinName] = useState('');
  const [practiceAlias, setPracticeAlias] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Reset form when dialog is closed
  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setTin('');
        setTinName('');
        setPracticeAlias('');
        setError('');
        setLoading(false);
      }, 200); // Delay to allow for closing animation
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await onSubmit({ tin, tinName, practiceAlias });
      onOpenChange(false); // Close dialog on success
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Add New Practice</DialogTitle>
            <DialogDescription>
              Register a new practice under the <span className="font-semibold text-primary">{organizationName}</span> organization.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="tinName" className="text-right">
                TIN Name
              </Label>
              <Input
                id="tinName"
                value={tinName}
                onChange={(e) => setTinName(e.target.value)}
                required
                className="col-span-3"
                placeholder="Official Name"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="tin" className="text-right">
                TIN #
              </Label>
              <Input
                id="tin"
                value={tin}
                onChange={(e) => setTin(e.target.value)}
                required
                maxLength={9}
                className="col-span-3"
                placeholder="9-digit TIN"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="practiceAlias" className="text-right">
                Alias
              </Label>
              <Input
                id="practiceAlias"
                value={practiceAlias}
                onChange={(e) => setPracticeAlias(e.target.value)}
                className="col-span-3"
                placeholder="Optional Nickname"
              />
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {loading ? 'Adding...' : 'Add Practice'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
